import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, ChevronLeft, Search, X, UserRound, Layers, DoorOpen, ListChecks, CalendarX2, Siren, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import DaySelector from "./DaySelector";
import ChecklistRow from "./ChecklistRow";
import { ymd, groupBy, compressToDataUrl, meseLabel, last12Months, pctColor, DOW_ABBR, fmtGiornoBreve, freqLabel } from "./helpers";

export default function PulizieChecklist({ structureId, data, setData, operatori = [], identity, activeOperatore }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canControllo = can("pulizie_controllo");
  const [filterOp, setFilterOp] = useState("all");        // assegnata a
  const [filterEseg, setFilterEseg] = useState("all");    // eseguita da
  const [filterStato, setFilterStato] = useState("all");  // stato
  const [filterArea, setFilterArea] = useState("all");    // zona
  const [filterMetodo, setFilterMetodo] = useState("all");
  const [q, setQ] = useState("");
  const [mine, setMine] = useState(false);
  const [clOpenDay, setClOpenDay] = useState(true);

  const { data: checklist = [], isLoading: clLoading } = useQuery({
    enabled: !!structureId,
    queryKey: ["pul-checklist", structureId, data],
    queryFn: async () => (await api.get("/pulizie/checklist", { params: { structure_id: structureId, data } })).data,
  });

  const { data: alert } = useQuery({
    enabled: !!structureId,
    queryKey: ["pul-alert", structureId, data],
    queryFn: async () => (await api.get("/pulizie/alert", { params: { structure_id: structureId, data } })).data,
  });
  const { data: straord = [] } = useQuery({
    enabled: !!structureId,
    queryKey: ["pul-straordinarie", structureId, data],
    queryFn: async () => (await api.get("/pulizie/straordinarie", { params: { structure_id: structureId, data, giorni: 15 } })).data,
  });
  const [showStraord, setShowStraord] = useState(true);
  const [showIeri, setShowIeri] = useState(false);
  const [showSett, setShowSett] = useState(false);
  const emergenzaAttiva = !!alert?.emergenza?.attiva;
  const soglia = alert?.soglia ?? 20;
  const pctSett = alert?.settimana?.pct ?? 0;
  const setEmergenza = async (attiva) => {
    try {
      await api.post("/pulizie/emergenza", { structure_id: structureId, attiva, pct: pctSett, operatore_nome: identity?.operatore_nome || "" });
      qc.invalidateQueries({ queryKey: ["pul-alert"] });
      qc.invalidateQueries({ queryKey: ["pul-emergenza"] });
      qc.invalidateQueries({ queryKey: ["sup-osp-giorno"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(attiva ? "Emergenza pulizie attivata" : "Emergenza pulizie risolta");
    } catch { toast.error("Errore"); }
  };

  const esegui = async (item, patch = {}) => {
    try {
      const fatto = patch.fatto !== undefined ? patch.fatto : item.fatto;
      await api.post("/pulizie/esegui", {
        attivita_id: item.attivita_id, data, structure_id: structureId, fatto,
        operatore_id: identity?.operatore_id, operatore_nome: identity?.operatore_nome,
        esito: patch.esito !== undefined ? patch.esito : (item.esito || ""),
        foto: patch.foto !== undefined ? patch.foto : (item.foto || ""),
        note: patch.note !== undefined ? patch.note : (item.note || ""),
      });
      qc.invalidateQueries({ queryKey: ["pul-checklist"] });
      qc.invalidateQueries({ queryKey: ["pul-mese"] });
    } catch { toast.error("Errore"); }
  };
  const toggle = (item) => esegui(item, { fatto: !item.fatto });
  const verifyOk = (item) => esegui(item, { fatto: true, esito: "ok", note: "" });
  const verifyKo = (item) => esegui(item, { fatto: false, esito: "da_rivedere" });
  const onFoto = async (item, file) => {
    if (!file) return;
    const foto = await compressToDataUrl(file);
    await esegui(item, { foto });
    toast.success("Foto allegata");
  };

  const doneCount = checklist.filter((c) => c.fatto).length;
  const areeOpt = [...new Set(checklist.map((c) => c.area_nome).filter(Boolean))].sort((a, b) => a.localeCompare(b, "it"));
  const esegOpt = [...new Set(checklist.filter((c) => c.fatto && c.operatore_nome).map((c) => c.operatore_nome))].sort((a, b) => a.localeCompare(b, "it"));
  const metodiOpt = [...new Set(checklist.map((c) => c.metodo).filter(Boolean))];

  const visible = checklist.filter((c) => {
    if (mine && activeOperatore && c.assegnato_id !== activeOperatore.id) return false;
    if (filterOp !== "all") { if (filterOp === "none") { if (c.assegnato_id) return false; } else if (c.assegnato_id !== filterOp) return false; }
    if (filterStato === "da_fare" && c.fatto) return false;
    if (filterStato === "fatte" && !c.fatto) return false;
    if (filterEseg !== "all" && c.operatore_nome !== filterEseg) return false;
    if (filterArea !== "all" && c.area_nome !== filterArea) return false;
    if (filterMetodo !== "all" && c.metodo !== filterMetodo) return false;
    if (q && !`${c.descrizione} ${c.elemento_nome || ""} ${c.stanza_nome || ""} ${c.area_nome || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const filtriAttivi = mine || filterOp !== "all" || filterEseg !== "all" || filterStato !== "all" || filterArea !== "all" || filterMetodo !== "all" || !!q;
  const resetFiltri = () => { setMine(false); setFilterOp("all"); setFilterEseg("all"); setFilterStato("all"); setFilterArea("all"); setFilterMetodo("all"); setQ(""); };
  const clGroups = groupBy(visible);

  if (!clOpenDay) return <ChecklistMonth structureId={structureId} onOpenDay={(d) => { setData(d); setClOpenDay(true); }} />;

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <Button variant="outline" size="sm" onClick={() => setClOpenDay(false)} data-testid="pul-cl-back"><ChevronLeft size={15} className="mr-1" /> Torna al mese</Button>
      </div>
      <DaySelector data={data} setData={setData} extra={<span className="text-sm font-semibold rounded-lg px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="pul-progress">{doneCount}/{checklist.length} completate</span>} />

      {emergenzaAttiva && (
        <div className="mb-4 rounded-xl border-2 border-red-500 bg-red-50 p-4 flex items-start gap-3 animate-pulse-slow" data-testid="pul-emergenza-banner">
          <Siren size={24} className="text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-head font-bold text-red-700 text-base">EMERGENZA PULIZIE ATTIVA</div>
            <div className="text-sm text-red-700">Troppe pulizie non completate nell'ultima settimana. Intervenire al più presto.{alert?.emergenza?.operatore ? ` · Segnalata da ${alert.emergenza.operatore}` : ""}</div>
          </div>
          <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100 shrink-0" onClick={() => setEmergenza(false)} data-testid="pul-emergenza-risolvi">Segna risolta</Button>
        </div>
      )}

      {alert && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4" data-testid="pul-alert-boxes">
          <div className="bg-white rounded-xl border p-3" data-testid="pul-box-ieri">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><CalendarX2 size={14} className="text-amber-600" /> Non completate ieri</span>
              <span className={`text-2xl font-head font-bold ${alert.ieri.count > 0 ? "text-amber-600" : "text-emerald-600"}`}>{alert.ieri.count}</span>
            </div>
            {alert.ieri.count > 0 && <button onClick={() => setShowIeri((s) => !s)} className="text-[11px] font-semibold text-sky-600 hover:underline mt-1" data-testid="pul-box-ieri-toggle">{showIeri ? "Nascondi" : "Vedi elenco"}</button>}
            {showIeri && <div className="mt-2 space-y-1 max-h-44 overflow-y-auto pr-1">{alert.ieri.items.map((it, i) => <div key={i} className="text-[12px] bg-amber-50 rounded px-2 py-1 leading-snug">{it.label}</div>)}</div>}
          </div>
          <div className="bg-white rounded-xl border p-3" data-testid="pul-box-settimana">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><AlertTriangle size={14} className="text-amber-600" /> Non completate (7 gg)</span>
              <span className={`text-2xl font-head font-bold ${alert.settimana.count > 0 ? "text-amber-600" : "text-emerald-600"}`}>{alert.settimana.count}</span>
            </div>
            {alert.settimana.count > 0 && <button onClick={() => setShowSett((s) => !s)} className="text-[11px] font-semibold text-sky-600 hover:underline mt-1" data-testid="pul-box-settimana-toggle">{showSett ? "Nascondi" : "Vedi elenco"}</button>}
            {showSett && <div className="mt-2 space-y-1 max-h-44 overflow-y-auto pr-1">{alert.settimana.items.map((it, i) => <div key={i} className="text-[12px] bg-amber-50 rounded px-2 py-1 leading-snug flex justify-between gap-2"><span className="min-w-0">{it.label}</span><span className="text-[10px] text-muted-foreground shrink-0">{(it.data || "").slice(8, 10)}/{(it.data || "").slice(5, 7)}</span></div>)}</div>}
          </div>
          <div className={`rounded-xl border p-3 ${pctSett > soglia ? "bg-red-50 border-red-300" : "bg-white"}`} data-testid="pul-box-pct">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500">% non fatte (7 gg)</span>
              <span className={`text-2xl font-head font-bold ${pctSett > soglia ? "text-red-600" : "text-slate-700"}`}>{pctSett}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className={`h-full ${pctSett > soglia ? "bg-red-500" : "bg-amber-400"}`} style={{ width: `${Math.min(100, pctSett)}%` }} /></div>
            {pctSett > soglia && !emergenzaAttiva && (
              <Button size="sm" className="w-full mt-2.5 bg-red-600 hover:bg-red-700 text-white font-bold" onClick={() => setEmergenza(true)} data-testid="pul-emergenza-attiva"><Siren size={15} className="mr-1" /> Attiva emergenza</Button>
            )}
            {pctSett > soglia && emergenzaAttiva && <div className="text-[11px] font-bold text-red-600 mt-2 flex items-center gap-1"><Siren size={12} /> Emergenza attiva</div>}
            {pctSett <= soglia && <div className="text-[11px] text-emerald-600 mt-2 flex items-center gap-1"><CheckCircle2 size={12} /> Sotto la soglia del {soglia}%</div>}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border-2 border-indigo-200 p-3 mb-4" data-testid="pul-straordinarie-box">
        <button onClick={() => setShowStraord((s) => !s)} className="w-full flex items-center gap-2 text-left">
          <CalendarX2 size={16} className="text-indigo-600 shrink-0" />
          <span className="font-head font-bold text-sm text-indigo-800">Pulizie non giornaliere dei prossimi 15 giorni</span>
          <span className="text-xs font-bold rounded-full px-2 py-0.5 bg-indigo-100 text-indigo-700 tabnum" data-testid="pul-straordinarie-count">{straord.length}</span>
          <span className="ml-auto text-[11px] font-semibold text-indigo-600">{showStraord ? "Nascondi" : "Mostra"}</span>
        </button>
        {showStraord && (
          straord.length === 0 ? (
            <div className="text-xs text-muted-foreground mt-2">Nessuna pulizia non giornaliera prevista nei prossimi 15 giorni.</div>
          ) : (
            <div className="mt-2 space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid="pul-straordinarie-list">
              {straord.map((it) => (
                <div key={it.attivita_id} className="flex items-start gap-2 text-[13px] leading-snug rounded-lg bg-indigo-50/60 px-2.5 py-1.5" data-testid={`pul-straord-${it.attivita_id}`}>
                  <span className="shrink-0 font-bold text-indigo-700 w-40">{fmtGiornoBreve(it.data)}</span>
                  <span className="min-w-0">
                    <b>{it.area_nome}</b>{it.stanza_nome ? ` · ${it.stanza_nome}` : ""}{it.elemento_nome ? ` · ${it.elemento_nome}` : ""} — {it.descrizione}
                    <span className="text-[11px] text-muted-foreground"> ({freqLabel(it)})</span>
                  </span>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {activeOperatore && (
        <button onClick={() => setMine((v) => !v)} data-testid="pul-le-mie-pulizie" className={`w-full sm:w-auto mb-4 flex items-center gap-2.5 rounded-xl border px-4 py-2.5 font-semibold text-sm transition-colors ${mine ? "bg-sky-600 text-white border-sky-600" : "bg-white hover:bg-sky-50 border-sky-200 text-sky-700"}`}>
          <UserRound size={17} /> Le mie pulizie
          <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${mine ? "bg-white/20" : "bg-sky-100 text-sky-700"}`}>{checklist.filter((c) => c.assegnato_id === activeOperatore.id).length}</span>
          {mine && <span className="text-[11px] font-normal opacity-90">· solo {activeOperatore.nome}</span>}
        </button>
      )}

      <div className="bg-white rounded-xl border p-3 mb-4">
        <div className="flex items-center gap-2 mb-2 text-xs font-bold text-slate-500 uppercase tracking-wide"><Search size={13} /> Filtri</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca attività, stanza, zona…" className="pl-8 h-9" data-testid="pul-filter-search" />
          </div>
          <Select value={filterStato} onValueChange={setFilterStato}>
            <SelectTrigger className="h-9" data-testid="pul-filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="da_fare">Da fare</SelectItem><SelectItem value="fatte">Completate</SelectItem></SelectContent>
          </Select>
          <Select value={filterArea} onValueChange={setFilterArea}>
            <SelectTrigger className="h-9" data-testid="pul-filter-area"><SelectValue placeholder="Zona" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le zone</SelectItem>{areeOpt.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterOp} onValueChange={setFilterOp}>
            <SelectTrigger className="h-9" data-testid="pul-filter-op"><SelectValue placeholder="Assegnata a" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Assegnata a: tutti</SelectItem>
              <SelectItem value="none">Non assegnate</SelectItem>
              {operatori.map((o) => <SelectItem key={o.id} value={o.id}>{o.nome} {o.cognome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterEseg} onValueChange={setFilterEseg}>
            <SelectTrigger className="h-9" data-testid="pul-filter-eseg"><SelectValue placeholder="Eseguita da" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Eseguita da: chiunque</SelectItem>{esegOpt.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterMetodo} onValueChange={setFilterMetodo}>
            <SelectTrigger className="h-9" data-testid="pul-filter-metodo"><SelectValue placeholder="Metodo" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti i metodi</SelectItem>{metodiOpt.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-xs text-muted-foreground" data-testid="pul-filter-count">{visible.length} di {checklist.length} attività mostrate · {visible.filter((c) => c.fatto).length} fatte</span>
          {filtriAttivi && <button onClick={resetFiltri} className="text-xs font-semibold text-sky-600 hover:underline flex items-center gap-1" data-testid="pul-filter-reset"><X size={12} /> Azzera filtri</button>}
        </div>
      </div>

      {clLoading ? <p className="text-muted-foreground">Caricamento…</p> : checklist.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground"><Sparkles className="mx-auto mb-3 opacity-40" size={40} /><p>Nessuna pulizia programmata per questa data.</p><p className="text-sm">Configura la mappa e le frequenze nella scheda "Configurazione mappa".</p></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-14 text-muted-foreground"><Search className="mx-auto mb-3 opacity-40" size={36} /><p>Nessuna attività corrisponde ai filtri.</p></div>
      ) : (
        <div className="space-y-5">
          <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_88px_minmax(170px,280px)_56px] gap-3 px-4 text-[11px] font-bold text-slate-400 uppercase tracking-wide">
            <div>Attività</div><div className="text-center">Completato</div><div>{canControllo ? "Verifica task" : "Esito verifica"}</div><div className="text-center">Foto</div>
          </div>
          {Object.entries(clGroups).map(([area, stanze]) => (
            <div key={area} className="bg-white rounded-xl border overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-50 border-b font-bold flex items-center gap-2"><Layers size={15} className="text-slate-500" /> {area}</div>
              <div className="divide-y">
                {Object.entries(stanze).map(([stanza, items]) => (
                  <div key={stanza} className="p-3">
                    {stanza !== "__area__" && <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1"><DoorOpen size={13} /> {stanza}</div>}
                    <div className="space-y-1.5">
                      {items.map((it) => (
                        <ChecklistRow key={it.attivita_id} it={it} onToggle={toggle} onVerifyOk={verifyOk} onVerifyKo={verifyKo} onNote={(item, note) => esegui(item, { note })} onFoto={onFoto} canControllo={canControllo} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChecklistMonth({ structureId, onOpenDay }) {
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const months = last12Months();
  const todayIso = ymd(new Date());
  const { data, isLoading } = useQuery({
    queryKey: ["pul-mese", structureId, mese],
    queryFn: async () => (await api.get("/pulizie/mese", { params: { structure_id: structureId, mese } })).data,
  });
  const giorni = data?.giorni || [];
  const tot = data?.totali || {};
  return (
    <div data-testid="pul-cl-month">
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
        {months.map((m) => (
          <button key={m} onClick={() => setMese(m)} data-testid={`pul-cl-mese-${m}`} className={`px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap shrink-0 border transition-colors capitalize ${mese === m ? "bg-sky-600 text-white border-sky-600" : "bg-white hover:bg-slate-50"}`}>{meseLabel(m)}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-xl border p-4"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Completamento</div><div className={`text-3xl font-head font-bold ${pctColor(tot.pct || 0, false).text}`}>{tot.pct || 0}%</div><div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className={`h-full ${pctColor(tot.pct || 0, false).bar}`} style={{ width: `${tot.pct || 0}%` }} /></div></div>
        <div className="bg-white rounded-xl border p-4"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Completate</div><div className="text-3xl font-head font-bold text-emerald-700">{tot.fatte || 0}</div><div className="text-[11px] text-muted-foreground">su {tot.totale || 0}</div></div>
        <div className="bg-white rounded-xl border p-4"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Da sistemare</div><div className="text-3xl font-head font-bold text-red-600">{tot.da_rivedere || 0}</div></div>
        <div className="bg-white rounded-xl border p-4"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Arretrate</div><div className="text-3xl font-head font-bold text-amber-600">{tot.arretrate || 0}</div></div>
      </div>
      <h3 className="font-head font-bold text-base mb-3 flex items-center gap-2"><ListChecks size={16} className="text-sky-600" /> Giorni di <span className="capitalize">{meseLabel(mese)}</span> <span className="text-xs font-normal text-muted-foreground">— clicca un giorno per aprire la checklist</span></h3>
      {isLoading ? <p className="text-muted-foreground">Caricamento…</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
          {giorni.map((g) => {
            const col = pctColor(g.pct, g.futuro);
            const vuoto = g.totale === 0;
            return (
              <button key={g.data} onClick={() => !vuoto && onOpenDay(g.data)} disabled={vuoto} data-testid={`pul-cl-day-${g.data}`}
                className={`text-left bg-white rounded-xl border p-3 transition-shadow ${vuoto ? "opacity-50 cursor-default" : "hover:shadow-md cursor-pointer"} ${g.data === todayIso ? "ring-2 ring-sky-400" : ""} ${col.ring}`}>
                <div className="flex items-baseline justify-between">
                  <div className="font-head font-bold text-lg leading-none">{g.giorno}<span className="text-[10px] font-semibold text-muted-foreground ml-1">{DOW_ABBR[g.dow]}</span></div>
                  {!vuoto && <span className={`text-xs font-bold ${col.text}`}>{g.pct}%</span>}
                </div>
                {vuoto ? <div className="text-[11px] text-muted-foreground mt-2">Nessuna pulizia</div> : (
                  <>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className={`h-full ${col.bar}`} style={{ width: `${g.pct}%` }} /></div>
                    <div className="text-[11px] text-muted-foreground mt-1.5">{g.fatte}/{g.totale} completate{g.futuro ? " (prev.)" : ""}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {g.da_rivedere > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{g.da_rivedere} da sistemare</span>}
                      {g.arretrate > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{g.arretrate} arretr.</span>}
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
